import { Store } from '@/store/mainStore';
import { action, autorun, computed, observable, reaction } from 'mobx';
import prescription, { Pair, Property } from '@/store/prescription/prescriptionDefinition';
import { OrthoticStyle, orthoticStylesArray } from '@/store/Products';
import { SessionStatusEnum } from '@/store/engine/openapi/generated';
import NewPrescription, { LROptions } from '@/store/prescription/prescription-types';
import initialPrescription from '@/store/prescription/prescription-initial.json';
import { Packs } from '@/store/ProductStore';

export type Prescription = typeof prescription;

export type KeyNewPrescription<T extends keyof NewPrescription> = keyof NewPrescription[T];

export interface SavedRxProperties {
	title: string;
	diagnosis: DiagnosisType;
}

export const diagnoses = ['Please select', 'Post-Tib Tendonitis', 'Metatarsalgia', 'Plantar Fasciitis'];
export type DiagnosisType = typeof diagnoses[number];

export default class PrescriptionStore {
	@observable private _prescription: Prescription = { ...prescription };
	@observable private _newPrescription: NewPrescription = { ...(initialPrescription as NewPrescription) }; // FIXME(PTYPE)
	@observable diagnosis: DiagnosisType = diagnoses[0];
	@observable updatedInThisSession = false;
	@observable prescriptionCreated = false;
	@observable allowUpdate: boolean = true;
	@observable showHelpSaverxModal: string = 'false';
	private autoSaveTimeout = 0;
	private generateInsolesSetTimeout = 0;

	store: Store;

	constructor(store: Store) {
		this.store = store;
		this.initWatchers();
	}

	private async initWatchers() {
		await this.watchPrescription(this.newPrescription);
	}

	static isSorted(arr: number[] | string[]) {
		for (let i = 1; i < arr.length; i += 1) {
			if (arr[i - 1] > arr[i]) {
				return false;
			}
		}
		return true;
	}

	@action setShowHelpSaverxModal(arg: string): void {
		this.showHelpSaverxModal = arg;
	}

	@action delayedSavePrescription(update = false, nats?: SessionStatusEnum) {
		clearTimeout(this.autoSaveTimeout);
		this.autoSaveTimeout = setTimeout(() => this.store.prescription.savePrescription(update, nats), 1000);
	}

	public async onPrescriptionChanged(delayedSave = true) {
		if (!this.store._patient.userRecordID) return;
		window.postMessage('patient.dirty', '*');
		await this.store.leroy.onPrescriptionChanged();
		if (delayedSave) {
			this.delayedSavePrescription(true);
		}
		this.updatedInThisSession = true;
	}

	/**
	 * Watch recursively for prescription changes
	 * @param toCheck object to check
	 * @private
	 */
	@action.bound
	private async watchPrescription(toCheck: Prescription | Property | NewPrescription) {
		// if (typeof toCheck === 'object') {
		// 	if ('value' in toCheck) {
		// 		if (typeof toCheck.value === 'object') {
		// 			reaction(
		// 				() => [toCheck.value.l, toCheck.value.r],
		// 				() => this.onPrescriptionChanged()
		// 			);
		// 		} else {
		// 			reaction(
		// 				() => toCheck.value,
		// 				() => this.onPrescriptionChanged()
		// 			);
		// 		}
		// 	}
		// 	for (const i in toCheck) {
		// 		this.watchPrescription(toCheck[i]);
		// 	}
		// }

		if (typeof toCheck === 'object') {
			for (const i in toCheck) {
				if (typeof toCheck[i] === 'string' || typeof toCheck[i] === 'number' || toCheck[i] === null) {
					reaction(
						() => toCheck[i],
						async (val) => {
							await this.onPrescriptionChanged();
						}
					);
				} else if (typeof toCheck[i] === 'object') {
					await this.watchPrescription(toCheck[i]);
				}
			}
		}
	}

	@action.bound resetPrescription() {
		this.newPrescription = initialPrescription as NewPrescription;
		this.updatedInThisSession = false;
	}

	validateLR(changedTo: LROptions): LROptions {
		switch (changedTo) {
			case 'both':
				return this.store.biometric.bothScansExist ? 'both' : this.validateLR(this.store.biometric.leftScanExists ? 'left' : 'right');
			case 'left':
				return this.store.biometric.leftScanExists ? 'left' : 'none';
			case 'right':
				return this.store.biometric.rightScanExists ? 'right' : 'none';
			default:
				return 'none';
		}
	}

	@action.bound
	public validatePrescriptionByScans(leftScanAvailable: boolean, rightScanAvailable: boolean) {
		if (leftScanAvailable && rightScanAvailable) return;
		this.validateNewPrescriptionByScans();
	}

	@action.bound
	private validateNewPrescriptionByScans() {
		// Shell modifications
		for (const item in this._newPrescription.shellModifications) {
			if (this._newPrescription.shellModifications[item]?.hasOwnProperty('side')) {
				// FIXME: DEMOHACK: should have side
				this._newPrescription.shellModifications[item].side = this.validateLR(this._newPrescription.shellModifications[item].side);
			}
		}

		// Posting
		for (const item in this._newPrescription.postingOptions) {
			if (
				this._newPrescription.postingOptions[item] &&
				this._newPrescription.postingOptions[item].hasOwnProperty('left') &&
				this._newPrescription.postingOptions[item].hasOwnProperty('right')
			) {
				if (this._newPrescription.postingOptions[item].override) {
					if (this._newPrescription.postingOptions[item].left?.value && this._newPrescription.postingOptions[item].right?.value) {
						if (this._newPrescription.postingOptions[item].left.value !== '0deg' && !this.store.biometric.leftScanExists) {
							this._newPrescription.postingOptions[item].left.value = '0deg';
						}
						if (this._newPrescription.postingOptions[item].right.value !== '0deg' && !this.store.biometric.rightScanExists) {
							this._newPrescription.postingOptions[item].right.value = '0deg';
						}
					}
				} else {
					if (this._newPrescription.postingOptions[item].left !== '0mm' && !this.store.biometric.leftScanExists) {
						this._newPrescription.postingOptions[item].left = '0mm';
					}
					if (this._newPrescription.postingOptions[item].right !== '0mm' && !this.store.biometric.rightScanExists) {
						this._newPrescription.postingOptions[item].right = '0mm';
					}
				}
			}
		}

		// Accommodations
		for (const item in this._newPrescription.accommodations) {
			if (typeof this._newPrescription.accommodations[item] === 'string') {
				this._newPrescription.accommodations[item] = this.validateLR(this._newPrescription.accommodations[item]);
			} else if (['neuromaPad', 'balancePad'].includes(item) && this._newPrescription.accommodations[item]) {
				if (!this.store.biometric.leftScanExists) this._newPrescription.accommodations[item].left = [];
				if (!this.store.biometric.rightScanExists) this._newPrescription.accommodations[item].right = [];
			} else {
				// FIXME: DEMOHACK: handle rest of fields
			}
		}
	}

	@action
	public async updatePrescription(update = false, nats?: SessionStatusEnum) {
		await this.store._patient.updateMedicalProfile({
			natsData: nats /* PrescriptionStatusEnum.PRESCRIPTION_UPDATED */,
			update
		});
		if (update) this.updatedInThisSession = false;
	}

	@action
	public async createPrescription() {
		this.prescriptionCreated = true;
		await this.store._patient.updateMedicalProfile({
			natsData: SessionStatusEnum.SESSION_STATUS_PRESCRIPTION_CREATED,
			update: true
		});
	}

	@action
	public async savePrescription(update = false, nats?: SessionStatusEnum) {
		if (!this.allowUpdate) return;
		if (this.prescriptionCreated || update) {
			// update
			await this.updatePrescription(update, nats);
			return;
		}

		// HACK: Note: Newly created prescription will always force SESSION_CREATED session state
		//create
		await this.createPrescription();

		await new Promise((r) => setTimeout(r, 1000));

		await this.updatePrescription();
	}

	// metadata and notes are glued to the solution profile here:
	public async getPrescription(prescription: NewPrescription | undefined | null) {
		if (!prescription) {
			return;
		}

		////// !!!!!!!!!!
		// this.store.utils.toggleWatcher(false);

		const { quantity, orthoticStyle, orthoticStyleTitle, pack, weight } = prescription.uiContextData;
		if (quantity !== undefined) {
			this.store.order.quantity = quantity;
		}
		if (orthoticStyle !== undefined) {
			this.store.product.setCurrentStyle(orthoticStyle);
		}
		if (orthoticStyleTitle !== undefined && orthoticStyleTitle !== '') {
			this.store.product.currentSavedRXTitle = orthoticStyleTitle;
		}
		if (pack !== undefined) {
			this.store.product.pack = ((pack: 'right' | 'left' | 'both'): Packs => {
				switch (pack) {
					case 'left':
						return 'Left Only';
					case 'right':
						return 'Right Only';
					default:
						return 'Pair';
				}
			})(pack);
		}
		this.store._patient.patientUIData.weight.value = parseInt(weight.value);
		this.store._patient.patientUIData.weight.type = weight.unit;
		this.newPrescription = prescription;
	}

	get prescription() {
		return this._prescription;
	}

	@computed get newPrescription() {
		return this._newPrescription;
	}

	set newPrescription(value: NewPrescription) {
		this._newPrescription = { ...value };
		this.initWatchers();
	}
}
